import React, { useState } from 'react';
import Select, { GroupBase, OptionProps } from 'react-select';
import { NXDownArrow, NXRightArrow, NXAlertCircle } from '../icons';
import CategoryModal from "../pages/category/modalForm";



interface Option {
  value: string;
  label: string;
  parent?: string;
  children?: Option[];
}

interface NestedSelectProps {
  name?: string;
  options?: Option[];
  defaultValue?: Option | Option[];
  defaultOption?: Option;
  placeholder?: string;
  onChange?: (value: any, actionMeta: any) => void;
  customStyles?: any;
  addComponentName?: string;
  label?: string;
  helpText?: string;
  infoClassName?: string;
  labelProps?: Record<string, any>;
  errorData?: any;
}



const defaultOptions: Option[] = [
  { value: 'flavors', label: 'Flavors', children: [
    { value: 'chocolate', label: 'Chocolate', children: [
      { value: 'dark-chocolate', label: 'Dark Chocolate' },
      { value: 'milk-chocolate', label: 'Milk Chocolate' },
      { value: 'white-chocolate', label: 'White Chocolate' },
    ]},
    { value: 'vanilla', label: 'Vanilla', children: [
      { value: 'french-vanilla', label: 'French Vanilla' },
      { value: 'madagascar-vanilla', label: 'Madagascar Vanilla' },
    ]},
    { value: 'strawberry', label: 'Strawberry', children: [
      { value: 'fresh-strawberry', label: 'Fresh Strawberry' },
      { value: 'strawberry-cheesecake', label: 'Strawberry Cheesecake' },
    ]},
  ]},
  { value: 'toppings', label: 'Toppings', children: [
    { value: 'nuts', label: 'Nuts', children: [
      { value: 'almonds', label: 'Almonds' },
      { value: 'peanuts', label: 'Peanuts' },
      { value: 'walnuts', label: 'Walnuts' },
    ]},
    { value: 'syrups', label: 'Syrups', children: [
      { value: 'chocolate-syrup', label: 'Chocolate Syrup' },
      { value: 'caramel-syrup', label: 'Caramel Syrup' },
      { value: 'strawberry-syrup', label: 'Strawberry Syrup' },
    ]},
    { value: 'fruits', label: 'Fruits', children: [
      { value: 'banana', label: 'Banana' },
      { value: 'cherry', label: 'Cherry' },
      { value: 'blueberry', label: 'Blueberry' },
    ]},
  ]},
];



const CustomOption = (props: OptionProps<Option, false, GroupBase<Option>>) => {
  const { data, innerRef, innerProps, selectOption } = props;
  const [isOpen, setIsOpen] = useState(false);


  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectOption(data);
  };

  return (
    <div ref={innerRef} {...innerProps} onClick={handleSelect}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className='cursor-pointer px-3 py-2 hover:bg-slate-200 w-full'>{data.label}</span>
        {data.children && (
          <span onClick={handleToggle} style={{ cursor: 'pointer' }}>
            {isOpen ? <NXDownArrow className='h-6'/> : <NXRightArrow className='h-6'/> }
          </span>
        )}
      </div>
      {isOpen && data.children && (
        <div style={{ paddingLeft: 20 }}>
          {data.children.map((child) => (
            <CustomOption
              key={child.value}
              {...props}
              data={child}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const NestedSelect: React.FC<NestedSelectProps> = ({
  name,
  options = defaultOptions,
  defaultValue,
  defaultOption = { value: '', label: 'Select an option' },
  placeholder = "Select an item",
  onChange,
  customStyles,
  addComponentName,
  label,
  helpText,
  infoClassName,
  labelProps,
  errorData,
  ...rest
}) => {
  const defaultVal = defaultValue || defaultOption;

  const [openDrawer, setOpenDrawer] = useState<string | null>(null);
  const errorMessages = name && errorData && errorData[name] ? errorData[name] : [];

  const onAddButtonClick = () => {
    if (addComponentName) {
      setOpenDrawer(addComponentName);
    }
  };

  const onDrawerModalClose = () => {
    setOpenDrawer(null);
  };

  return (
    <div className="flex flex-col">
         {label && (
        <div className="flex items-center space-x-2 mb-1">
          <label htmlFor={name} {...labelProps} className="text-sm font-medium text-gray-700">
            {label}
          </label>
          <div className="relative group inline-block">
            {/* Help Icon */}
            <NXAlertCircle className={infoClassName ? infoClassName : 'text-base-300'} />
            {/* Tooltip */}
            {helpText && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                {helpText}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Select component taking the full available width */}
      <div className="flex items-center">
        <div className="flex-grow">
          <Select<Option>
            options={options}
            defaultValue={defaultVal}
            placeholder={placeholder}
            components={{ Option: CustomOption }}
            onChange={onChange}
            styles={customStyles}
            {...rest}
          />

          {errorMessages.length > 0 && (
            <div className="text-red-600 text-sm mt-1">
              {errorMessages.map((error: any, index: number) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}
        </div>

      {/* Add button aligned to the right of the Select component */}
      {addComponentName && (
        <button
          type="button"
          onClick={onAddButtonClick}
          className="ml-1 w-7 h-7 flex items-center justify-center text-black bg-white border border-gray-400 rounded-full transition-all duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
        >
          +
        </button>
      )}
      </div>

    <CategoryModal refresh={() => {}} isOpen={openDrawer === "category"} onClose={onDrawerModalClose} />
    </div>
  );
};

export default NestedSelect;
